/**
 * WarehouseMultiSelect — F1 (plan „shared frontend foundations for the
 * product-picker modal"). Thin wrapper around the generic `MultiSelect`
 * primitive (`@/components/MultiSelect`), retargeted to the warehouses list
 * fetched via `useWarehouses`.
 *
 * Empty-selection semantics: an empty `value` means "no warehouse filter" —
 * i.e. ALL warehouses — never "none". `MultiSelect`'s own `placeholder` prop
 * already only renders when nothing is selected, so passing
 * `ui.warehouseMultiSelect.allSelected` there is enough to surface that
 * semantic directly in the trigger.
 *
 * Module-gating / empty catalog: when `useWarehouses` degrades to `[]`
 * (module not active, no permission, or the tenant genuinely has no
 * warehouses yet), the control disables itself and shows
 * `ui.warehouseMultiSelect.noWarehouses` instead of the normal placeholder —
 * never crashes, never shows an empty-but-interactive dropdown.
 *
 * Real fetch failure vs. "no warehouses": `useWarehouses` rethrows non-402/403
 * errors (network, 500) as `isError` — that is NOT the same situation as an
 * empty catalog and must not render identically to it (mirrors how
 * `ProductSearchPicker` separates its `loadError` state from `noResults`).
 * When `isError` is true the control shows `ui.warehouseMultiSelect.loadError`
 * and stays disabled.
 */

import { useTranslation } from 'react-i18next';

import { MultiSelect, type MultiSelectOption } from '@/components/MultiSelect';
import { useWarehouses } from '@/hooks/useWarehouses';

export interface WarehouseMultiSelectProps {
  companyId?: string | null;
  /** Selected warehouse ids — empty = all warehouses (see module doc above). */
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

export function WarehouseMultiSelect({
  companyId,
  value,
  onChange,
  disabled = false,
  'data-testid': testId,
}: WarehouseMultiSelectProps) {
  const { t } = useTranslation();
  const { warehouses, isLoading, isError } = useWarehouses({ companyId, enabled: true });

  const options: MultiSelectOption[] = warehouses.map((warehouse) => ({
    value: warehouse.id,
    label: `${warehouse.code} — ${warehouse.name}`,
  }));

  // Gdy lista jest pusta PO zakończeniu ładowania (moduł nieaktywny, brak
  // uprawnienia albo tenant naprawdę nie ma jeszcze magazynów) — nie ma czego
  // wybierać, więc blokujemy kontrolkę zamiast pokazywać pusty, klikalny dropdown.
  const hasNoWarehouses = !isLoading && !isError && options.length === 0;

  const placeholder = isError
    ? t('ui.warehouseMultiSelect.loadError')
    : hasNoWarehouses
      ? t('ui.warehouseMultiSelect.noWarehouses')
      : t('ui.warehouseMultiSelect.allSelected');

  return (
    <div data-testid={testId}>
      <MultiSelect
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        disabled={disabled || hasNoWarehouses || isError}
        data-testid={testId ? `${testId}-select` : undefined}
      />
    </div>
  );
}
